import React, { createContext, useState } from "react";
import { api, apiVanilla, RouterOutputs } from "@/lib/trpc/client";
import { Card } from "@/components/ui/card";
import { Icon } from "@iconify/react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { RegistratureFormsTable } from "@/components/registrature/RegistratureFormsTable";
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem } from "@/components/ui/context-menu";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";
import { DialogAction } from "@/components/form-builder/form-components/DialogAction";
import { useTranslations } from "use-intl";
import OfficialMonitorTree from "./components/OfficialMonitorTree";
import { Data } from "./components/types";
export const OfficialMonitorContext = createContext<{
  institutionId: string;
  groups: RouterOutputs["officialMonitor"]["publicRouter"]["getOfficialMonitorGroups"];
}>({
  institutionId: "",
  groups: []
});
interface Registrature {
  id: string;
  displayName: string;
  startNumber: number;
  institutionId: string | null;
}
export interface Group {
  id: string;
  displayName: string;
  type: string;
  registrature?: Registrature | null;
  children: Group[];
}
interface FileDialogProps {
  fileId: string | null;
  open: boolean;
  isPublicView?: boolean;
  onOpenChange: (open: boolean) => void;
}
const FileDialog: React.FC<FileDialogProps> = ({
  fileId,
  open,
  onOpenChange,
  isPublicView
}) => {
  const {
    data: registrature
  } = api.registratureRouter.getRegistratureById.useQuery({
    id: fileId || ""
  }, {
    enabled: !!fileId
  });
  return <Dialog open={open} onOpenChange={onOpenChange} data-sentry-element="Dialog" data-sentry-component="FileDialog" data-sentry-source-file="OfficialMonitorTab.tsx">
      <DialogContent className="max-w-6xl" data-sentry-element="DialogContent" data-sentry-source-file="OfficialMonitorTab.tsx">
        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="OfficialMonitorTab.tsx">{registrature?.displayName ?? "File"}</DialogTitle>
        {registrature ? <RegistratureFormsTable registratureId={registrature.id} registrature={registrature} isDialogView /> : <p className="mt-2">No entries.</p>}
      </DialogContent>
    </Dialog>;
};
interface TreeNodeProps {
  group: Group;
  onRegistratureClick?: (registratureId: string) => void;
  isPublicView?: boolean;
  onAddGroup?: (parentId: string) => void;
  onAddRegistrature?: (groupId: string, registratureId: string) => void;
  onEditGroup?: (groupId: string, newName: string) => void;
  onDeleteGroup?: (groupId: string) => void;
}
const TreeNode: React.FC<TreeNodeProps> = ({
  group,
  onRegistratureClick,
  isPublicView,
  onAddGroup,
  onAddRegistrature,
  onEditGroup,
  onDeleteGroup
}) => {
  const isGroup = group.type === "group";
  if (isGroup) {
    if (isPublicView) {
      return <Card className="p-6 shadow-none">
          <Card className="bg-glow p-2 px-6 py-4 font-semibold text-card">
            <div>{group.displayName || <em>No title</em>}</div>
          </Card>
          {group.children.some(child => child.type === "registrature") && <div className="mt-4 grid grid-cols-1 gap-4 mobile:grid-cols-2 tablet:grid-cols-3">
              {group.children.filter(child => child.type === "registrature").map(child => <div key={child.id}>
                    <Card className="h-14 min-h-32 cursor-pointer overflow-hidden border p-6 shadow-none transition-all hover:border-glow hover:bg-glow/5 hover:text-glow" onClick={() => child.registrature && onRegistratureClick?.(child.registrature.id)}>
                      <div className="flex h-full w-full flex-col items-start justify-between">
                        <Icon icon="fluent-mdl2:registry-editor" className="text-3xl" />
                        <div>
                          {child.registrature?.displayName || child.displayName || <em>No title</em>}
                        </div>
                      </div>
                      <RegistratureDocumentsCount registratureId={child?.registrature?.id} />
                    </Card>
                  </div>)}
            </div>}
          {group.children.some(child => child.type === "group") && <div className="ml-4 mt-4">
              {group.children.filter(child => child.type === "group").map(child => <TreeNode key={child.id} group={child} onRegistratureClick={onRegistratureClick} onAddGroup={onAddGroup} onAddRegistrature={onAddRegistrature} onEditGroup={onEditGroup} onDeleteGroup={onDeleteGroup} isPublicView={isPublicView} />)}
            </div>}
        </Card>;
    }

    // Non-public view: group nodes with context menu actions
    return <ContextMenu>
        <ContextMenuTrigger>
          <Card className="p-6">
            <Card className="bg-glow p-2 px-6 py-4 font-semibold text-card">
              <div>{group.displayName || <em>No title</em>}</div>
            </Card>
            {group.children.some(child => child.type === "registrature") && <div className="mt-4 grid grid-cols-1 gap-4 mobile:grid-cols-2 tablet:grid-cols-3">
                {group.children.filter(child => child.type === "registrature").map(child => <div key={child.id}>
                      <ContextMenu>
                        <ContextMenuTrigger>
                          <Card className="h-14 min-h-32 cursor-pointer overflow-hidden border p-6 shadow-none transition-all hover:border-glow hover:bg-glow/5 hover:text-glow" onClick={() => child.registrature && onRegistratureClick?.(child.registrature.id)}>
                            <div className="flex h-full w-full flex-col items-start justify-between">
                              <Icon icon="fluent-mdl2:registry-editor" className="text-3xl" />
                              <div>
                                {child.registrature?.displayName || child.displayName || <em>No title</em>}
                              </div>
                            </div>
                            <RegistratureDocumentsCount registratureId={child?.registrature?.id} />
                          </Card>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          <ContextMenuItem onClick={() => child.registrature && onRegistratureClick?.(child.registrature.id)}>
                            Vizualizează registratura
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => {
                    // Replace with delete registrature handler if available
                    console.log("Delete registratura", child.id);
                  }}>
                            Șterge registratura
                          </ContextMenuItem>
                          <ContextMenuItem onClick={() => {
                    // Replace with edit registrature handler if available
                    console.log("Edit registratura", child.id);
                  }}>
                            Editează
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                    </div>)}
              </div>}
            {group.children.some(child => child.type === "group") && <div className="ml-4 mt-4">
                {group.children.filter(child => child.type === "group").map(child => <TreeNode key={child.id} group={child} onRegistratureClick={onRegistratureClick} onAddGroup={onAddGroup} onAddRegistrature={onAddRegistrature} onEditGroup={onEditGroup} onDeleteGroup={onDeleteGroup} isPublicView={isPublicView} />)}
              </div>}
          </Card>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onClick={() => {
          const newName = prompt("Enter new name for the group", group.displayName);
          if (newName && onEditGroup) {
            onEditGroup(group.id, newName);
          }
        }}>
            Editează
          </ContextMenuItem>
          <ContextMenuItem onClick={() => onAddGroup && onAddGroup(group.id)}>
            Adaugă subgrup
          </ContextMenuItem>
          <ContextMenuItem onClick={() => {
          const registratureId = prompt("Enter registrature id to add");
          if (registratureId && onAddRegistrature) {
            onAddRegistrature(group.id, registratureId);
          }
        }}>
            Adaugă registratură
          </ContextMenuItem>
          <ContextMenuItem onClick={() => onDeleteGroup && onDeleteGroup(group.id)}>
            Șterge
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>;
  }
  if (isPublicView) {
    return <div className="my-2 ml-4">
        <Card className="h-14 min-h-32 cursor-pointer border p-6 transition-all hover:border-glow hover:bg-glow/5" onClick={() => group.registrature && onRegistratureClick?.(group.registrature.id)}>
          <div className="flex h-full w-full flex-col items-start justify-between">
            <Icon icon="fluent-mdl2:registry-editor" className="text-3xl" />
            <div>{group.registrature?.displayName || group.displayName || <em>No title</em>}</div>
          </div>
        </Card>
      </div>;
  }
  return <div className="my-2 ml-4" data-sentry-component="TreeNode" data-sentry-source-file="OfficialMonitorTab.tsx">
      <ContextMenu data-sentry-element="ContextMenu" data-sentry-source-file="OfficialMonitorTab.tsx">
        <ContextMenuTrigger data-sentry-element="ContextMenuTrigger" data-sentry-source-file="OfficialMonitorTab.tsx">
          <Card className="h-14 min-h-32 cursor-pointer overflow-hidden border p-6 transition-all hover:border-glow hover:bg-glow/5" onClick={() => group.registrature && onRegistratureClick?.(group.registrature.id)} data-sentry-element="Card" data-sentry-source-file="OfficialMonitorTab.tsx">
            <div className="flex h-full w-full flex-col items-start justify-between">
              <Icon icon="fluent-mdl2:registry-editor" className="text-3xl" data-sentry-element="Icon" data-sentry-source-file="OfficialMonitorTab.tsx" />
              <div>{group.registrature?.displayName || group.displayName || <em>No title</em>}</div>
            </div>
            <RegistratureDocumentsCount registratureId={group.registrature?.id} data-sentry-element="RegistratureDocumentsCount" data-sentry-source-file="OfficialMonitorTab.tsx" />
          </Card>
        </ContextMenuTrigger>
        <ContextMenuContent data-sentry-element="ContextMenuContent" data-sentry-source-file="OfficialMonitorTab.tsx">
          <ContextMenuItem onClick={() => group.registrature && onRegistratureClick?.(group.registrature.id)} data-sentry-element="ContextMenuItem" data-sentry-source-file="OfficialMonitorTab.tsx">
            Vizualizează registratura
          </ContextMenuItem>
          <ContextMenuItem onClick={() => {
          console.log("Edit registratura", group.id);
        }} data-sentry-element="ContextMenuItem" data-sentry-source-file="OfficialMonitorTab.tsx">
            Editează
          </ContextMenuItem>
          <ContextMenuItem onClick={() => {
          console.log("Delete registratura", group.id);
        }} data-sentry-element="ContextMenuItem" data-sentry-source-file="OfficialMonitorTab.tsx">
            Șterge registratura
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </div>;
};
interface OfficialMonitorTabProps {
  institutionId: string;
  isPublicView?: boolean;
}
export const OfficialMonitorTab: React.FC<OfficialMonitorTabProps> = ({
  institutionId,
  isPublicView
}) => {
  const t = useTranslations();
  const importGroupsMutation = api.officialMonitor.adminRouter.importGroups.useMutation();
  const {
    data: groupsData,
    refetch
  } = api.officialMonitor.publicRouter.getOfficialMonitorGroups.useQuery({
    institutionId
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [isEditView, setIsEditView] = useState(false);
  const addEditGroupMutation = api.officialMonitor.adminRouter.addEditGroup.useMutation();
  const deleteGroupMutation = api.officialMonitor.adminRouter.deleteGroup.useMutation();
  if (!groupsData) {
    return <div>Loading...</div>;
  }
  const categories = groupsData.map(group => ({
    id: group.id,
    name: group.displayName
  }));
  const selectedCategory = groupsData.find(group => group.id === selectedCategoryId);
  const selectedCategoryChildren = selectedCategory?.children || [];
  const handleRegistratureClick = (registratureId: string) => {
    setSelectedFileId(registratureId);
    setIsFileDialogOpen(true);
  };
  const copyStructure = async () => {
    const groups = await apiVanilla.officialMonitor.adminRouter.getGroups.query({
      institutionId
    });
    navigator.clipboard.writeText(JSON.stringify(groups));
    await toast.success("Structura a fost copiată cu succes");
  };
  const importGroupsFromClipboard = async () => {
    const clipboardData = await navigator.clipboard.readText();
    let groups;
    try {
      groups = JSON.parse(clipboardData);
    } catch (error) {
      await toast.error("Datele din clipboard nu sunt valide");
      return;
    }
    await importGroupsMutation.mutateAsync({
      institutionId,
      groups
    });
    await toast.success("Grupurile au fost importate cu succes");
    await refetch();
  };
  const importGroupsWrapper = () => {
    if (!groupsData.length) {
      importGroupsFromClipboard();
    } else {
      setShowWarning(true);
    }
  };
  const handleAddRegistratureInsideNode = (groupId: string, registratureId: string) => {
    addEditGroupMutation.mutate({
      id: groupId,
      registratureId,
      institutionId
    }, {
      onSuccess: () => refetch()
    });
  };
  const handleAddGroupInsideNode = (parentId: string) => {
    addEditGroupMutation.mutate({
      parentId,
      displayName: "New group",
      institutionId
    }, {
      onSuccess: () => refetch()
    });
  };
  const onRename = async ({
    name,
    id,
    parentId
  }: {
    name: string;
    id: string;
    parentId: string;
  }) => {
    addEditGroupMutation.mutate({
      id,
      displayName: name,
      parentId,
      institutionId
    });
  };
  const onDeleteFolder = (id: string) => {
    deleteGroupMutation.mutate({
      id
    }, {
      onSuccess: () => {
        refetch();
      }
    });
  };
  const handleEditGroup = (groupId: string, newName: string) => {
    onRename({
      name: newName,
      id: groupId,
      parentId: ""
    });
  };
  const handleDeleteGroup = (groupId: string) => {
    onDeleteFolder(groupId);
  };
  function convertToData(items: any[]): Data[] {
    return items.map(item => {
      const converted: Data = {
        id: item.id,
        name: item.name || item.displayName || item.registrature?.name || item.registrature?.displayName,
        children: convertToData(item.children || []),
        type: item.type
      };
      if (item.registratureId) {
        converted.registratureId = item.registratureId;
      }
      return converted;
    });
  }
  return <OfficialMonitorContext.Provider value={{
    institutionId,
    groups: groupsData
  }} data-sentry-element="unknown" data-sentry-component="OfficialMonitorTab" data-sentry-source-file="OfficialMonitorTab.tsx">
      {!isPublicView && <div className="flex items-center justify-between gap-2">
          <Button className="h-14 gap-2 bg-blue-600 text-white hover:bg-blue-700" onClick={() => setIsEditView(prev => !prev)}>
            {isEditView ? <>
                <Icon icon="lets-icons:view-fill" className="h-6 w-6" />
                Vizualizează
              </> : <>
                <Icon icon="mynaui:edit-one-solid" className="h-6 w-6" />
                Editează
              </>}
          </Button>
          <div className="flex gap-2">
            <Button className="h-14 gap-2 bg-blue-600 text-white hover:bg-blue-700" onClick={copyStructure}>
              <Icon icon="bi:upload" className="h-6 w-6" />
              {t("src_app_dashboard_admin_institutions_id_official_monitor_OfficialMonitorTab_tsx_1")}
            </Button>
            <Button className="h-14 gap-2 bg-blue-600 text-white hover:bg-blue-700" onClick={importGroupsWrapper}>
              <Icon icon="oui:import" className="h-6 w-6" />
              {t("src_app_dashboard_admin_institutions_id_official_monitor_OfficialMonitorTab_tsx_2")}
            </Button>
          </div>
        </div>}
      {!isEditView ? <>
          {isPublicView ? <Card className="mt-4 p-6 shadow-none">
              <div className="grid grid-cols-2 gap-4 desktop:grid-cols-4">
                {categories.map(category => <Card key={category.id} className={`flex h-14 min-h-32 cursor-pointer items-center justify-center rounded-3xl border bg-card font-semibold text-card-foreground shadow-none transition-all hover:border-glow hover:bg-card/80 hover:text-glow ${selectedCategoryId === category.id ? "border-glow bg-card text-glow hover:bg-card/80" : ""}`} onClick={() => setSelectedCategoryId(category.id)}>
                    <div className="flex h-full w-full flex-col items-start justify-between p-5">
                      <Icon icon="fluent:folder-16-filled" className="text-3xl" />
                      <div>{category.name}</div>
                    </div>
                  </Card>)}
              </div>
            </Card> : <ContextMenu>
              <ContextMenuTrigger>
                <Card className="mt-4 p-6">
                  <div className="grid grid-cols-2 gap-4 desktop:grid-cols-4">
                    {categories.map(category => <ContextMenu key={category.id}>
                        <ContextMenuTrigger>
                          <Card className={`flex h-14 min-h-32 cursor-pointer items-center justify-center rounded-3xl border bg-card font-semibold text-card-foreground shadow-none transition-all hover:border-glow hover:bg-card/80 hover:text-glow ${selectedCategoryId === category.id ? "border-glow bg-card text-glow hover:bg-card/80" : ""}`} onClick={() => setSelectedCategoryId(category.id)}>
                            <div className="flex h-full w-full flex-col items-start justify-between p-5">
                              <Icon icon="fluent:folder-16-filled" className="text-3xl" />
                              <div>{category.name}</div>
                            </div>
                          </Card>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          <ContextMenuItem>Editează</ContextMenuItem>
                          <ContextMenuItem>Adaugă subgrup</ContextMenuItem>
                          <ContextMenuItem>Adaugă registratură</ContextMenuItem>
                          <ContextMenuItem>Șterge</ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>)}
                  </div>
                </Card>
              </ContextMenuTrigger>
              <ContextMenuContent>
                <ContextMenuItem>Adaugă categorie</ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>}

          {selectedCategory && <div className="mt-4 flex flex-col gap-4">
              {selectedCategoryChildren.length > 0 ? selectedCategoryChildren.map((group: Group) => <TreeNode key={group.id} group={group} onRegistratureClick={handleRegistratureClick} onAddGroup={handleAddGroupInsideNode} onAddRegistrature={handleAddRegistratureInsideNode} onEditGroup={handleEditGroup} onDeleteGroup={handleDeleteGroup} isPublicView={isPublicView} />) : <div>No groups available.</div>}
            </div>}
        </> : <div className="mt-4">
          <OfficialMonitorTree officialMonGroups={convertToData(groupsData)} institutionId={institutionId} refetchGroups={refetch} viewOnly={false} />
        </div>}

      <FileDialog fileId={selectedFileId} open={isFileDialogOpen} onOpenChange={setIsFileDialogOpen} isPublicView={isPublicView} data-sentry-element="FileDialog" data-sentry-source-file="OfficialMonitorTab.tsx" />

      <DialogAction open={showWarning} onOpenChange={open => setShowWarning(open)} acceptText="Confirmă" acceptCallback={async () => {
      await importGroupsFromClipboard();
      setShowWarning(false);
    }} title="Confirmă importul" description="Aveți grupuri deja existente. Dacă continuați, acestea vor fi șterse și înlocuite cu cele noi. Sigur doriți să continuați?" data-sentry-element="DialogAction" data-sentry-source-file="OfficialMonitorTab.tsx" />
    </OfficialMonitorContext.Provider>;
};
const RegistratureDocumentsCount = ({
  registratureId
}: {
  registratureId?: string | null;
}) => {
  const {
    data: documentsCount,
    isLoading,
    isPending,
    isFetching
  } = api.registratureRouter.getRegistratureRecordsCount.useQuery({
    id: registratureId || "",
    year: new Date().getFullYear()
  }, {
    enabled: !!registratureId
  });
  return <div className="absolute right-0 top-0 flex min-h-10 min-w-10 items-center justify-center rounded-bl-2xl rounded-tr-2xl bg-glow p-1 text-card" data-sentry-component="RegistratureDocumentsCount" data-sentry-source-file="OfficialMonitorTab.tsx">
      {isLoading || isPending || isFetching ? <Icon icon="svg-spinners:90-ring" className="text-lg" /> : <span className="px-2 py-1">{documentsCount || 0} documente</span>}
    </div>;
};